/* InlineField — a fact that is edited in place, one at a time (spec/03 "Inline field editing"):
   the VALUE IS THE CONTROL. At rest it is a `<button>` showing the value (or "Not specified"),
   named "Edit: {field}" for assistive tech (`aria-haspopup="dialog"`, `aria-expanded`), with a light
   hover wash that bleeds past the text (negative margin, so the text stays aligned with its label)
   and a 14px pencil right after the text — always visible, so the affordance needs no hover to be
   found. A reader without the right to write gets the plain value: no button, no hover, no pencil.
   `clamp` (a line count) cuts a long value to that many lines and puts a "Show in full" / "Collapse"
   link UNDER the value, outside the button (a button cannot hold a button, and expanding must not
   open the editor); the link exists only while the cut removes something — measured, and
   re-measured when the text or the width changes.

   Activating it opens the field's own control OVER the card, so nothing below shifts — an antd
   `Popover` (controlled `open`, `bottomLeft`, flipping up near the bottom edge, as wide as the
   field and at least 270px), or below `sm` an antd `Drawer` from the bottom with the field's name
   as its title. Inside: the control (`children`), then the hint or the error, then a footer — the
   key hint on the left ("Enter · Esc", or "Ctrl+Enter · Esc" for a multi-line field), Cancel and
   the one primary Save on the right. Save is `loading` while the request runs and disabled until
   something changed. Focus goes into the control on open and back to the value button on close.

   Keys, on the editor (so every control type gets them without wiring each): Esc cancels; Enter
   saves a single-line field; Ctrl/⌘+Enter saves a multi-line one (plain Enter is a new line there).
   Enter is NOT a save while an antd Select's or DatePicker's dropdown is open — there it picks the
   option / commits the typed day; the next Enter, with the list closed, saves — and that one is
   taken in the capture phase, because rc-select / rc-picker would otherwise re-open the list on it.
   An IME composition's Enter is ignored too.

   A click outside the editor closes it when nothing changed, and does nothing when something has
   (a draft is never lost to a stray click — Cancel and Esc are explicit). `locked` is "another
   field holds unsaved changes": the button is `aria-disabled`, says why in its tooltip, and ignores
   activation; the one-at-a-time rule is the caller's, this only shows it.

   Save and Cancel act immediately: a single field's draft is on screen and cheap to redo, so there
   is no "discard?" confirm here — the page-level leave guard covers navigating away. */

import React, { useEffect, useRef, useState } from "react";
import { Button, Drawer, Flex, Grid, Popover, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";

import "./inline-field.scss";

const MIN_EDITOR_WIDTH = 270;
const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), button:not([disabled])';

/** Is `target` inside an antd Select or Picker whose dropdown is open (Enter / Esc belong to it)? */
function dropdownOpen(target) {
  if (!(target instanceof Element)) return false;
  if (target.closest(".ant-select-open")) return true;
  if (target.closest(".ant-picker")) {
    /* rc-picker keeps its dropdown mounted after the first open and hides it by class — after a
       ~250ms leave motion, during which it carries a `*-leave` class and is already closed. */
    const panel = document.querySelector(".ant-picker-dropdown:not(.ant-picker-dropdown-hidden)");
    return Boolean(panel) && ![...panel.classList].some(c => c.endsWith("-leave"));
  }
  return false;
}

/* The editor's body: the control, the hint / error line, and (in the popover) the footer. Mounted
   only while open (`destroyOnHidden`), so mounting is opening — focus goes to the first focusable
   thing inside the control here. */
function EditorBody({ id, label, width, children, hint, error, busy, dirty, multiline, onSave, onCancel, t, footer = true }) {
  const ref = useRef(null);
  useEffect(() => {
    const first = ref.current?.querySelector(".ifv-ed__control")?.querySelector(FOCUSABLE);
    first?.focus({ preventScroll: true });
  }, []);

  /* Esc, in the bubble phase: an open dropdown takes it first (rc closes the list), the next one
     cancels. */
  const onKeyDown = event => {
    if (event.key !== "Escape" || event.nativeEvent?.isComposing) return;
    if (dropdownOpen(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    if (!busy) onCancel();
  };
  /* Enter, in the CAPTURE phase, so the control never sees the press that saves: rc-select and
     rc-picker both OPEN their list on Enter when it is closed, which would flash a dropdown under a
     closing editor. With the list open the press is theirs (pick / commit); the buttons act
     themselves; a multi-line field saves on Ctrl/⌘+Enter only. */
  const onKeyDownCapture = event => {
    if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
    if (event.target instanceof Element && event.target.closest("button")) return;
    if (multiline ? !(event.ctrlKey || event.metaKey) : dropdownOpen(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    if (dirty && !busy) onSave();
  };

  return (
    <div
      className="ifv-ed"
      ref={ref}
      role="dialog"
      aria-label={label}
      style={width ? { width } : undefined}
      onKeyDown={onKeyDown}
      onKeyDownCapture={onKeyDownCapture}>
      <div className="ifv-ed__control">{children}</div>
      {error ? (
        <div className="ifv-ed__error" id={`${id}-error`} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="ifv-ed__hint" id={`${id}-hint`}>
          {hint}
        </div>
      ) : null}
      {footer ? (
        <Flex className="ifv-ed__foot" align="center" justify="space-between" gap={8} wrap>
          <span className="ifv-ed__keys" aria-hidden="true">
            {t(multiline ? "inline_keys_multi" : "inline_keys_single")}
          </span>
          <Flex gap={8}>
            <Button disabled={busy} onClick={onCancel}>
              {t("edit_cancel")}
            </Button>
            <Button type="primary" loading={busy} disabled={!dirty} onClick={onSave}>
              {t("edit_save")}
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </div>
  );
}

export default function InlineField({
  id,
  label,
  value,
  children,
  editing = false,
  locked = false,
  canEdit = true,
  onStart,
  onCancel,
  onSave,
  busy = false,
  dirty = false,
  error,
  hint,
  multiline = false,
  clamp,
  t,
}) {
  const screens = Grid.useBreakpoint();
  const mobile = screens.sm === false;
  const buttonRef = useRef(null);
  const wasEditing = useRef(editing);

  /* On close, focus returns to the value button (the trigger). The popover / drawer animates out,
     so at this point the control that held focus is still in the DOM — focus is taken back from it
     (or from `body`) — but never from something outside the editor that already took it, as the
     next field's editor does when one open-but-clean field yields to another. */
  useEffect(() => {
    if (!editing && wasEditing.current) {
      const active = document.activeElement;
      if (!active || active === document.body || active.closest(".ifv-ed, .ant-drawer")) buttonRef.current?.focus({ preventScroll: true });
    }
    wasEditing.current = editing;
  }, [editing]);

  const empty = value === null || value === undefined || value === "";
  const shown = empty ? <span className="ifv__empty">{t("value_empty")}</span> : value;

  /* The clamp: cut while collapsed, measured for the link; expanded keeps the last measurement so
     the link stays to collapse again. */
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const clamped = Boolean(clamp) && !empty && !expanded;
  /* A multi-line value (a text field, a clamped text) aligns to its FIRST line: the pencil stands
     beside line one, not at the middle of the block; everything else centres in the control-high box. */
  const top = multiline || Boolean(clamp);
  useEffect(() => {
    const el = textRef.current;
    if (!clamped || !el) return undefined;
    const measure = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    measure();
    /* The web font lands after the first layout and re-wraps the text without changing the clamped
       box's height, so a resize observer alone misses it: measure again once the fonts are in. */
    let live = true;
    document.fonts?.ready.then(() => live && measure());
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      live = false;
      ro?.disconnect();
    };
  }, [clamped, value]);
  const text = (
    <span ref={textRef} className={clamped ? "ifv__text ifv__text--clamp" : "ifv__text"} style={clamped ? { WebkitLineClamp: clamp } : undefined}>
      {shown}
    </span>
  );
  const more =
    clamp && !empty && (clipped || expanded) ? (
      <Button type="link" size="small" className="ifv__more" aria-expanded={expanded} onClick={() => setExpanded(open => !open)}>
        {t(expanded ? "show_less" : "show_more")}
      </Button>
    ) : null;

  if (!canEdit) {
    return (
      <div className="ifv-cell">
        <div className={top ? "ifv ifv--static ifv--top" : "ifv ifv--static"}>{text}</div>
        {more}
      </div>
    );
  }

  /* A click outside (or the drawer's mask / ×): nothing changed → close; a draft → stay. */
  const onOpenChange = next => {
    if (next || busy) return;
    if (!dirty) onCancel();
  };
  const activate = () => {
    if (editing || busy) return;
    if (!locked) onStart();
  };

  const trigger = (
    <button
      ref={buttonRef}
      type="button"
      className={`ifv ifv__btn${top ? " ifv--top" : ""}${editing ? " ifv--open" : ""}${locked ? " ifv--locked" : ""}`}
      aria-label={t("inline_edit_aria").replace("{field}", label)}
      aria-haspopup="dialog"
      aria-expanded={editing}
      aria-disabled={locked || undefined}
      onClick={activate}>
      {text}
      <EditOutlined className="ifv__pen" aria-hidden="true" />
    </button>
  );
  const button = locked && !editing ? <Tooltip title={t("inline_locked_tip")}>{trigger}</Tooltip> : trigger;
  /* As wide as the value cell (the button's parent), never under 270px — measured at render while
     open; the drawer is as wide as the screen. */
  const width = editing && !mobile ? Math.max(MIN_EDITOR_WIDTH, buttonRef.current?.parentElement?.getBoundingClientRect().width || 0) : undefined;
  const body = (
    <EditorBody
      id={id}
      label={label}
      width={width}
      hint={hint}
      error={error}
      busy={busy}
      dirty={dirty}
      multiline={multiline}
      onSave={onSave}
      onCancel={onCancel}
      t={t}
      footer={!mobile}>
      {children}
    </EditorBody>
  );

  if (mobile) {
    return (
      <div className="ifv-cell">
        {button}
        {more}
        <Drawer
          open={editing}
          placement="bottom"
          title={label}
          destroyOnHidden
          onClose={() => onOpenChange(false)}
          classNames={{ root: "ifv-drawer" }}
          styles={{ wrapper: { height: "auto" } }}
          footer={
            <Flex gap={8} justify="flex-end">
              <Button disabled={busy} onClick={onCancel}>
                {t("edit_cancel")}
              </Button>
              <Button type="primary" loading={busy} disabled={!dirty} onClick={onSave}>
                {t("edit_save")}
              </Button>
            </Flex>
          }>
          {editing ? body : null}
        </Drawer>
      </div>
    );
  }

  return (
    <div className="ifv-cell">
      <Popover open={editing} trigger="click" placement="bottomLeft" arrow={false} destroyOnHidden onOpenChange={onOpenChange} content={body}>
        {button}
      </Popover>
      {more}
    </div>
  );
}
